import { HeightOutlined, Refresh, Search } from '@mui/icons-material';
import { Autocomplete, Button, FormHelperText, Grid, InputLabel, TextField } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { skuSearch } from '../../service/Service';
import { AppContext } from '../../store/AppProvider';
import { SHOW_ERROR_ALERT, SHOW_WARNING_ALERT } from '../../store/reducer/AlertNotifierReducer';
import { convertDate, isDate, isEmptyObj, withoutTime } from '../../utils/Utils';
import { FieldTypes } from '../CustomFormField/CustomFormField';
import { CustomFormField } from '../component';
import './EntitlementSearchForm.css';
import { LoadingButton } from '@mui/lab';
import { ErrorConstant, Warnings } from '../../utils/Constants';
import dayjs from 'dayjs';
import { ReactMultiEmail } from 'react-multi-email';

export const EntitlementSearchForm = ({ onSearch, loading }) => {
    const { setAlert, entitlementSearchParams, setEntitlementSearchParams } = useContext(AppContext)
    const {
        serial_number: serialNumber,
        product_sku: productSKU,
        email: entitledEmail,
        ent_create_date_to: entCreateDateTo,
        ent_create_date_from: entCreateDateFrom,
        ent_start_date_to: entStartDateTo,
        ent_start_date_from: entStartDateFrom,
        ent_end_date_to: entEndDateTo,
        ent_end_date_from: entEndDateFrom,
        entitlement_type: entType
    } = entitlementSearchParams

    const [emails, setEmails] = useState(entitledEmail || [])
    const [emailsError, setEmailsError] = useState('')
    const [searchResults, setSearchResults] = useState([]);
    const [typingTimeout, setTypingTimeout] = useState(0);

    const {
        control,
        handleSubmit,
        reset,
        resetField,
        watch,
        setError,
        setValue,
        formState: { errors, dirtyFields, touchedFields }
    } = useForm({
        defaultValues: {
            serial_number: serialNumber || "",
            product_sku: productSKU || "",
            email: entitledEmail || "",
            ent_create_date_to: Boolean(entCreateDateTo) ? dayjs(entCreateDateTo) : null,
            ent_create_date_from: Boolean(entCreateDateFrom) ? dayjs(entCreateDateFrom) : null,
            ent_start_date_to: Boolean(entStartDateTo) ? dayjs(entStartDateTo) : null,
            ent_start_date_from: Boolean(entStartDateFrom) ? dayjs(entStartDateFrom) : null,
            ent_end_date_to: Boolean(entEndDateTo) ? dayjs(entEndDateTo) : null,
            ent_end_date_from: Boolean(entEndDateFrom) ? dayjs(entEndDateFrom) : null,
            entitlement_type: entType || ""
        }
    });

    const selectedEntCreateDateFrom = watch('ent_create_date_from')
    const selectedEntStartDateFrom = watch('ent_start_date_from')
    const selectedEntEndDateFrom = watch('ent_end_date_from')

    useEffect(() => {
        const { ent_create_date_from, ent_start_date_from, ent_end_date_from } = touchedFields
        if (!selectedEntCreateDateFrom && ent_create_date_from) {
            setValue('ent_create_date_to', null)
        }
        if (!selectedEntStartDateFrom && ent_start_date_from) {
            setValue('ent_start_date_to', null)
        }
        if (!selectedEntEndDateFrom && ent_end_date_from) {
            setValue('ent_end_date_to', null)
        }
    }, [selectedEntCreateDateFrom, selectedEntEndDateFrom, selectedEntStartDateFrom, setValue, touchedFields])

    const resetForm = () => {
        setEntitlementSearchParams({})
        if (emails?.length) setEmails([])
        reset({
            serial_number: "",
            product_sku: "",
            email: "",
            ent_create_date_to: null,
            ent_create_date_from: null,
            ent_start_date_to: null,
            ent_start_date_from: null,
            ent_end_date_to: null,
            ent_end_date_from: null,
            entitlement_type: ""
        })
    }

    const handleProductSearch = async (event) => {
        const searchText = event.target.value;
        clearTimeout(typingTimeout);

        const timeOutId = setTimeout(async () => {
            if (searchText.length > 2) {
                try {
                    const fetchedData = await skuSearch(searchText);
                    setSearchResults(fetchedData);
                } catch (error) {
                    setAlert({ type: SHOW_ERROR_ALERT, alertMessage: ErrorConstant.error })
                }
            }
        }, 300)
        setTypingTimeout(timeOutId)
    }

    const resetDateFields = (formData) => {
        const {
            ent_create_date_from,
            ent_create_date_to,
            ent_start_date_from,
            ent_start_date_to,
            ent_end_date_from,
            ent_end_date_to } = formData
        if (!ent_create_date_from) {
            formData.ent_create_date_to = null
            resetField('ent_create_date_to')
        }
        else {
            formData.ent_create_date_to = convertDate(ent_create_date_to)
        }
        if (!ent_start_date_from) {
            formData.ent_start_date_to = null
            resetField('ent_start_date_to')
        } else {
            formData.ent_start_date_to = convertDate(ent_start_date_to)
        }
        if (!ent_end_date_from) {
            formData.ent_end_date_to = null
            resetField('ent_end_date_to')
        } else {
            formData.ent_end_date_to = convertDate(ent_end_date_to)
        }
    }

    const notValidData = (formData) => {
        const {
            ent_create_date_from,
            ent_create_date_to,
            ent_start_date_from,
            ent_start_date_to,
            ent_end_date_from,
            ent_end_date_to } = formData
        let isNotValid = false;
        if (Boolean(ent_create_date_from)) {
            if (!isDate(ent_create_date_from)) {
                isNotValid = true
                setError('ent_create_date_from', { type: 'custom', message: 'Wrong date format' })
            }
            if (!Boolean(ent_create_date_to)) {
                isNotValid = true
                setError('ent_create_date_to', { type: 'required', message: 'To date is required' })
            } else {
                if (isDate(ent_create_date_to)) {
                    if (withoutTime(ent_create_date_to) < withoutTime(ent_create_date_from)) {
                        setError('ent_create_date_to', { type: 'custom', message: 'Date cannot be past date' })
                        isNotValid = true
                    }
                } else {
                    isNotValid = true
                    setError('ent_create_date_to', { type: 'custom', message: 'Wrong date format' })
                }
            }
        }
        if (Boolean(ent_start_date_from)) {
            if (!isDate(ent_start_date_from)) {
                isNotValid = true
                setError('ent_start_date_from', { type: 'custom', message: 'Wrong date format' })
            }
            if (!Boolean(ent_start_date_to)) {
                isNotValid = true
                setError('ent_start_date_to', { type: 'required', message: 'To date is required' })
            } else {
                if (isDate(ent_start_date_to)) {
                    if (withoutTime(ent_start_date_to) < withoutTime(ent_start_date_from)) {
                        setError('ent_start_date_to', { type: 'custom', message: 'Date cannot be past date' })
                        isNotValid = true
                    }
                } else {
                    isNotValid = true
                    setError('ent_start_date_to', { type: 'custom', message: 'Wrong date format' })
                }
            }
        }
        if (Boolean(ent_end_date_from)) {
            if (!isDate(ent_end_date_from)) {
                isNotValid = true
                setError('ent_end_date_from', { type: 'custom', message: 'Wrong date format' })
            }
            if (!Boolean(ent_end_date_to)) {
                isNotValid = true
                setError('ent_end_date_to', { type: 'required', message: 'To date is required' })
            } else {
                if (isDate(ent_end_date_to)) {
                    if (withoutTime(ent_end_date_to) < withoutTime(ent_end_date_from)) {
                        setError('ent_end_date_to', { type: 'custom', message: 'Date cannot be past date' })
                        isNotValid = true
                    }
                } else {
                    isNotValid = true
                    setError('ent_end_date_to', { type: 'custom', message: 'Wrong date format' })
                }
            }
        }
        return isNotValid;
    }

    const handleSearch = async (formData) => {
        const {
            ent_create_date_from,
            ent_start_date_from,
            ent_end_date_from,
            entitlement_type } = formData
        resetDateFields(formData)
        formData.ent_create_date_from = convertDate(ent_create_date_from)
        formData.ent_start_date_from = convertDate(ent_start_date_from)
        formData.ent_end_date_from = convertDate(ent_end_date_from)
        formData.email = emails
        formData.entitlement_type = entitlement_type || 'all'
        if (isEmptyObj(entitlementSearchParams)) {
            if (isEmptyObj(dirtyFields) && !emails?.length) {
                setAlert({ type: SHOW_WARNING_ALERT, alertMessage: Warnings.selectOneFilter })
                return
            }
        }
        if (notValidData(formData)) {
            return
        }
        onSearch(formData)
        setEntitlementSearchParams(formData)
    }

    const onError = (formData) => { }

    return (
        <form
            onSubmit={handleSubmit(handleSearch, onError)}
            noValidate
            autoComplete="off">
            <Grid container columnSpacing={8} marginBottom={1} >
                <Grid item xs={12} md={4}>
                    <CustomFormField label='Serial Number' control={control} name={"serial_number"} />
                </Grid>
                <Grid item xs={12} md={4}>
                    <InputLabel shrink htmlFor='product_sku' style={{ fontSize: 20, fontWeight: 'bold' }}>Product SKU</InputLabel>
                    <Controller
                        name="product_sku"
                        control={control}
                        render={({ field: { onChange, value: currValue } }) => (
                            <Autocomplete
                                freeSolo
                                disableClearable
                                onInputChange={(event, newValue, reason) => {
                                    onChange(newValue);
                                    setSearchResults([])
                                }}
                                options={searchResults}
                                sx={{
                                    "& .MuiInputBase-root": { height: "35px", backgroundColor: 'white' },
                                }}
                                getOptionLabel={(option) => option?.sku_name}
                                inputValue={currValue}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        id="product_sku"
                                        InputLabelProps={{ shrink: true }}
                                        error={false}
                                        value={currValue || ''}
                                        onChange={handleProductSearch}
                                    />
                                )}
                            />)}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <>
                        <InputLabel error={Boolean(emailsError)} shrink htmlFor={'email'} style={{ fontSize: 20, fontWeight: 'bold' }}>
                            {'Email'}
                        </InputLabel>
                        <ReactMultiEmail
                            placeholder={''}
                            emails={emails}
                            onChange={(_emails) => {
                                if (_emails.length <= 5) {
                                    setEmails(_emails)
                                    if (Boolean(emailsError)) {
                                        setEmailsError('')
                                    }
                                } else {
                                    setEmailsError('maximum limit reached!')
                                }
                            }}
                            getLabel={(
                                email,
                                index,
                                removeEmail
                            ) => {
                                return (
                                    <div data-tag key={index}>
                                        {email}
                                        <span data-tag-handle onClick={() => removeEmail(index)}>
                                            ×
                                        </span>
                                    </div>
                                );
                            }}
                        />
                        <FormHelperText error={Boolean(emailsError)}>{emailsError}</FormHelperText>
                    </>
                </Grid>
            </Grid>
            <Grid container columnSpacing={8} marginBottom={1} >
                <Grid item xs={12} md={4}>
                    <InputLabel error={Boolean(errors?.ent_create_date_from?.message || errors?.ent_create_date_to?.message)} shrink style={{ fontSize: 20, fontWeight: 'bold' }}>Entitlement Created Between</InputLabel>
                    <Grid container >
                        <Grid item xs={12} md={5} >
                            <CustomFormField errorMessage={errors?.ent_create_date_from?.message} type={FieldTypes.date} fontSize={11} name="ent_create_date_from" control={control} style={{ fontSize: 11 }} />
                        </Grid>
                        <Grid item xs={12} md={2}  >
                            <HeightOutlined style={{ width: '100%', color: 'rgba(0, 0, 0,0.6)', transform: 'rotate(90deg)' }} fontSize='large' />
                        </Grid>
                        <Grid item xs={12} md={5} >
                            <CustomFormField
                                type={FieldTypes.date}
                                fontSize={11}
                                name="ent_create_date_to"
                                errorMessage={errors?.ent_create_date_to?.message}
                                minDate={selectedEntCreateDateFrom}
                                disabled={!selectedEntCreateDateFrom}
                                control={control}
                                style={{ fontSize: 11 }} />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} md={4}>
                    <InputLabel error={Boolean(errors?.ent_start_date_from?.message || errors?.ent_start_date_to?.message)} shrink style={{ fontSize: 20, fontWeight: 'bold' }}>Entitlement Start Between</InputLabel>
                    <Grid container >
                        <Grid item xs={12} md={5} >
                            <CustomFormField errorMessage={errors?.ent_start_date_from?.message} type={FieldTypes.date} fontSize={11} name="ent_start_date_from" control={control} style={{ fontSize: 11 }} />
                        </Grid>
                        <Grid item xs={12} md={2} >
                            <HeightOutlined style={{ width: '100%', color: 'rgba(0, 0, 0,0.6)', transform: 'rotate(90deg)' }} fontSize='large' />
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <CustomFormField
                                type={FieldTypes.date}
                                fontSize={11}
                                name="ent_start_date_to"
                                errorMessage={errors?.ent_start_date_to?.message}
                                minDate={selectedEntStartDateFrom}
                                disabled={!selectedEntStartDateFrom}
                                control={control}
                                style={{ fontSize: 11 }} />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} md={4}>
                    <InputLabel error={Boolean(errors?.ent_end_date_from?.message || errors?.ent_end_date_to?.message)} shrink style={{ fontSize: 20, fontWeight: 'bold' }}>Entitlement End Between</InputLabel>
                    <Grid container >
                        <Grid item xs={12} md={5} >
                            <CustomFormField errorMessage={errors?.ent_end_date_from?.message} type={FieldTypes.date} fontSize={11} name="ent_end_date_from" control={control} style={{ fontSize: 11 }} />
                        </Grid>
                        <Grid item xs={12} md={2} >
                            <HeightOutlined style={{ width: '100%', color: 'rgba(0, 0, 0,0.6)', transform: 'rotate(90deg)' }} fontSize='large' />
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <CustomFormField
                                type={FieldTypes.date}
                                fontSize={11}
                                name="ent_end_date_to"
                                errorMessage={errors?.ent_end_date_to?.message}
                                minDate={selectedEntEndDateFrom}
                                disabled={!selectedEntEndDateFrom}
                                control={control}
                                style={{ fontSize: 11 }} />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Grid container columnSpacing={8} marginBottom={1} >
                <Grid item xs={12} md={4}>
                    <CustomFormField label='Entitlement Type' control={control} name={"entitlement_type"} type={FieldTypes.select} options={['all', 'warranty', 'limited_warranty']} />
                </Grid>
                <Grid item xs={12} md={4} />
            </Grid>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: 25 }}>
                <LoadingButton
                    loading={loading}
                    loadingPosition="start"
                    startIcon={<Search />}
                    variant="contained"
                    type='submit'
                    size="medium"
                    style={{ minWidth: '12%' }}
                >
                    Search
                </LoadingButton>
                <Button startIcon={<Refresh />} variant="outlined" size="medium" style={{ marginLeft: 20, minWidth: '12%', backgroundColor: 'white' }} onClick={() => resetForm()}>Reset</Button >
            </div>
        </form >
    )
}
